import posthog, { type PostHog } from "posthog-js";

const APP_NAME = "zipme-scheduler";
const APP_SOURCE = "web";

const SUPER_PROPERTIES = {
  app: APP_NAME,
  source: APP_SOURCE,
} as const;

type EventProperties = Record<string, unknown>;
type AnalyticsClient = Pick<PostHog, "capture" | "register"> | null | undefined;

export type FormQuestionLike = {
  id?: string | number;
  type?: string | null;
  label?: string | null;
};

export type RawUserContext = {
  name?: unknown;
  email?: unknown;
  phone?: unknown;
};

export type UserContextInput = {
  formValues?: Record<string, unknown> | null;
  questions?: FormQuestionLike[] | null;
  rawUser?: RawUserContext | null;
};

export type TrackPageOpenedInput = {
  pageName?: string;
  extra?: EventProperties;
};

export type TrackCtaClickedInput = UserContextInput & {
  ctaId: string;
  location: string;
  step?: string;
  ctaLabel?: string;
  extra?: EventProperties;
};

export type TrackCtaEventInput = TrackCtaClickedInput & {
  eventName?: string;
};

let hasInitializedPostHog = false;

const isBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

const shouldLogLocalPostHogDebug = (): boolean => {
  const forceLocal = process.env.REACT_APP_POSTHOG_FORCE_LOCAL;
  return forceLocal === "true" || forceLocal === "1";
};

export const isPostHogTrackingEnabled = (): boolean => {
  if (shouldLogLocalPostHogDebug()) {
    return true;
  }

  const appEnv = process.env.REACT_APP_ENV;
  if (appEnv) return appEnv === "production";
  return process.env.NODE_ENV === "production";
};

export const initSchedulerPostHog = (): PostHog => {
  if (hasInitializedPostHog) {
    return posthog;
  }

  if (!isPostHogTrackingEnabled()) {
    return posthog;
  }

  const apiKey = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;
  if (!apiKey) {
    return posthog;
  }

  posthog.init(apiKey, {
    api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
  });

  posthog.register({ ...SUPER_PROPERTIES });
  hasInitializedPostHog = true;

  return posthog;
};

const normalizeString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const normalizeKey = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const normalizePropertyKey = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const toRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
};

type PrefillParseResult = {
  prefillPresent: boolean;
  prefillParseError: boolean;
  prefillData: Record<string, unknown>;
};

export const parsePrefillFromCurrentUrl = (): PrefillParseResult => {
  if (!isBrowser()) {
    return {
      prefillPresent: false,
      prefillParseError: false,
      prefillData: {},
    };
  }

  const rawPrefill = new URLSearchParams(window.location.search).get("prefill");
  if (!rawPrefill) {
    return {
      prefillPresent: false,
      prefillParseError: false,
      prefillData: {},
    };
  }

  const parseAttempts = [
    () => JSON.parse(decodeURIComponent(rawPrefill)),
    () => JSON.parse(rawPrefill),
  ];

  for (const parse of parseAttempts) {
    try {
      const parsed = parse();
      return {
        prefillPresent: true,
        prefillParseError: false,
        prefillData: toRecord(parsed) || {},
      };
    } catch (error) {
      // Try the next parsing strategy.
    }
  }

  return {
    prefillPresent: true,
    prefillParseError: true,
    prefillData: {},
  };
};

type NormalizedUserFields = {
  name?: string;
  email?: string;
  phone?: string;
};

const buildUserFieldMeta = (
  user: NormalizedUserFields,
  prefill: PrefillParseResult
): EventProperties => {

  return omitUndefined({
    prefill_present: prefill.prefillPresent,
    prefill_parse_error: prefill.prefillParseError,
    name: user.name,
    email: user.email,
    phone: user.phone,
  });
};

const looksLikeNameKey = (normalizedKey: string): boolean => {
  return (
    normalizedKey === "name" ||
    normalizedKey === "fullname" ||
    normalizedKey === "contactname" ||
    normalizedKey === "customername"
  );
};

const looksLikeEmailKey = (normalizedKey: string): boolean => {
  return (
    normalizedKey === "email" ||
    normalizedKey === "emailaddress" ||
    normalizedKey === "contactemail" ||
    normalizedKey === "customeremail"
  );
};

const looksLikePhoneKey = (normalizedKey: string): boolean => {
  return (
    normalizedKey === "phone" ||
    normalizedKey === "phonenumber" ||
    normalizedKey === "contactno" ||
    normalizedKey === "contactnumber" ||
    normalizedKey === "mobileno" ||
    normalizedKey === "mobilenumber" ||
    normalizedKey === "mobile" ||
    normalizedKey === "contactphone" ||
    normalizedKey === "ztcontactphonenumber"
  );
};

const extractUserFieldsFromRecord = (
  record?: Record<string, unknown> | null
): NormalizedUserFields => {
  const extracted: NormalizedUserFields = {};
  if (!record) return extracted;

  Object.entries(record).forEach(([key, value]) => {
    const normalizedValue = normalizeString(value);
    if (!normalizedValue) return;

    const normalizedKey = normalizeKey(key);
    if (!extracted.name && looksLikeNameKey(normalizedKey)) {
      extracted.name = normalizedValue;
      return;
    }
    if (!extracted.email && looksLikeEmailKey(normalizedKey)) {
      extracted.email = normalizedValue;
      return;
    }
    if (!extracted.phone && looksLikePhoneKey(normalizedKey)) {
      extracted.phone = normalizedValue;
    }
  });

  return extracted;
};

const extractUserFieldsFromRawUser = (
  rawUser?: RawUserContext | null
): NormalizedUserFields => {
  if (!rawUser) return {};
  return {
    name: normalizeString(rawUser.name),
    email: normalizeString(rawUser.email),
    phone: normalizeString(rawUser.phone),
  };
};

const extractUserFieldsFromForm = (
  formValues?: Record<string, unknown> | null,
  questions?: FormQuestionLike[] | null
): NormalizedUserFields => {
  if (!formValues) return {};

  const extracted: NormalizedUserFields = {};

  const maybeAssign = (field: keyof NormalizedUserFields, value: unknown) => {
    const normalizedValue = normalizeString(value);
    if (!normalizedValue) return;
    if (!extracted[field]) {
      extracted[field] = normalizedValue;
    }
  };

  (questions || []).forEach((question) => {
    const qid =
      question?.id === undefined || question?.id === null
        ? undefined
        : String(question.id);
    const label = question?.label || undefined;
    const type = String(question?.type || "").toLowerCase();

    const value =
      (qid ? formValues[qid] : undefined) ??
      (label ? formValues[label] : undefined);

    if (!value) return;

    if (type === "zt_contact_name" || type === "zt_customer_name") {
      maybeAssign("name", value);
      return;
    }
    if (type === "zt_contact_email" || type === "email") {
      maybeAssign("email", value);
      return;
    }
    if (type === "zt_contact_phone_number" || type === "phone_number") {
      maybeAssign("phone", value);
      return;
    }

    const normalizedLabel = normalizeKey(label || "");
    if (looksLikeNameKey(normalizedLabel)) {
      maybeAssign("name", value);
      return;
    }
    if (looksLikeEmailKey(normalizedLabel)) {
      maybeAssign("email", value);
      return;
    }
    if (looksLikePhoneKey(normalizedLabel)) {
      maybeAssign("phone", value);
    }
  });

  const fallbackFromKeys = extractUserFieldsFromRecord(formValues);

  return {
    name: extracted.name || fallbackFromKeys.name,
    email: extracted.email || fallbackFromKeys.email,
    phone: extracted.phone || fallbackFromKeys.phone,
  };
};

const normalizeFormFieldValue = (value: unknown): unknown => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  }
  if (
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const buildFormFieldsContext = (
  formValues?: Record<string, unknown> | null,
  questions?: FormQuestionLike[] | null
): EventProperties => {
  if (!formValues) return {};

  const questionById = new Map<string, FormQuestionLike>();
  (questions || []).forEach((question) => {
    if (question?.id === undefined || question?.id === null) return;
    questionById.set(String(question.id), question);
  });

  const result: EventProperties = {};

  Object.entries(formValues).forEach(([rawKey, rawValue]) => {
    const value = normalizeFormFieldValue(rawValue);
    if (value === undefined) return;

    const question = questionById.get(String(rawKey));
    const labelSource =
      normalizeString(question?.label) ||
      (isNaN(Number(rawKey)) ? rawKey : "");
    const normalizedLabel = normalizePropertyKey(labelSource || "");
    const baseKey = normalizedLabel ? `form_${normalizedLabel}` : `form_field_${rawKey}`;

    let propertyKey = baseKey;
    if (Object.prototype.hasOwnProperty.call(result, propertyKey)) {
      propertyKey = `${baseKey}_${String(rawKey)}`;
    }

    result[propertyKey] = value;
  });

  return result;
};

export const buildUserContext = (input?: UserContextInput): EventProperties => {
  const prefill = parsePrefillFromCurrentUrl();
  const prefillUser = extractUserFieldsFromRecord(prefill.prefillData);
  const rawUser = extractUserFieldsFromRawUser(input?.rawUser);
  const formUser = extractUserFieldsFromForm(input?.formValues, input?.questions);

  const mergedUser: NormalizedUserFields = {
    name: formUser.name || rawUser.name || prefillUser.name,
    email: formUser.email || rawUser.email || prefillUser.email,
    phone: formUser.phone || rawUser.phone || prefillUser.phone,
  };

  return buildUserFieldMeta(mergedUser, prefill);
};

export const inferPageNameFromPathname = (pathname: string): string => {
  if (!pathname) return "unknown";

  if (pathname.includes("/cancel-meeting")) return "cancel_meeting";
  if (pathname.includes("/reschedule-meeting")) return "reschedule_meeting";
  if (pathname.includes("/realtime") || pathname.includes("/instant")) return "realtime";
  if (pathname.startsWith("/manage/")) return "manage";

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 1 || segments.length >= 2) return "scheduled";

  return "unknown";
};

export const getPageContext = (pageName?: string): EventProperties => {
  if (!isBrowser()) {
    return {
      page_id: "",
      pathname: "",
      app: APP_NAME,
      source: APP_SOURCE,
    };
  }

  const pathname = window.location.pathname;

  return omitUndefined({
    page_id: pathname,
    page_name: pageName || inferPageNameFromPathname(pathname),
    pathname,
    current_url: window.location.href,
    referrer: document.referrer || undefined,
    ...SUPER_PROPERTIES,
  });
};

const omitUndefined = <T extends EventProperties>(input: T): T => {
  const entries = Object.entries(input).filter(([, value]) => value !== undefined);
  return Object.fromEntries(entries) as T;
};

const capture = (
  client: AnalyticsClient,
  eventName: string,
  properties: EventProperties
) => {
  if (!isPostHogTrackingEnabled()) return;
  if (!client?.capture) return;
  if (shouldLogLocalPostHogDebug()) {
    console.log("[scheduler-posthog]", eventName, properties);
  }
  client.capture(eventName, properties);
};

export const trackPageOpened = (
  client: AnalyticsClient,
  input?: TrackPageOpenedInput
) => {
  const payload = omitUndefined({
    ...getPageContext(input?.pageName),
    ...buildUserContext(),
    ...(input?.extra || {}),
  });

  capture(client, "page_opened", payload);
};

export const trackCtaEvent = (
  client: AnalyticsClient,
  input: TrackCtaEventInput
) => {
  const payload = omitUndefined({
    cta_id: input.ctaId,
    location: input.location,
    step: input.step,
    cta_label: input.ctaLabel,
    ...getPageContext(),
    ...buildUserContext({
      formValues: input.formValues,
      questions: input.questions,
      rawUser: input.rawUser,
    }),
    ...buildFormFieldsContext(input.formValues, input.questions),
    ...(input.extra || {}),
  });

  capture(client, input.eventName || "cta_clicked", payload);
};

export const trackCtaClicked = (
  client: AnalyticsClient,
  input: TrackCtaClickedInput
) => {
  trackCtaEvent(client, {
    ...input,
    eventName: "cta_clicked",
  });
};
